import { useEffect, useRef, useState } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import UIButton from 'UIComponents/button/UIButton';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import DataPanelPropertyIcon from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPanelProperty/DataPanelPropertyIcon';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import EditPropertyLink from 'automation-ui-references/object-type-properties/EditPropertyLink';
import { useIsUngated, useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import { ENFORCE_FIELD_LEVEL_PERMISSIONS, SUPPORT_SELECTING_SENSITIVE_PROPERTIES } from 'automation-ui-formula-editor/constants/Gates';
import { hasFlpAccessToDataProperty } from 'automation-ui-formula-editor/data-sources/utils/hasFlpAccessToDataProperty';
import { getFlpDisabledPropertyMessage } from 'automation-ui-core/utils/getFlpDisabledPropertyMessage';
import { Text } from 'foundations-components/components/Text';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { InfoIcon } from 'foundations-assets';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { getIsRestrictedSensitiveProperty } from 'automation-ui-formula-editor/data-sources/utils/getIsRestrictedSensitiveProperty';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledPropertyDiv = styled.div.withConfig({
  displayName: "DataPanelProperty__StyledPropertyDiv",
  componentId: "sc-1lbjahd-0"
})(({
  theme
}) => {
  return {
    position: 'relative',
    insetInlineEnd: `${theme.space[900]}`,
    insetBlockEnd: `${theme.space[250]}`,
    blockSize: '40px',
    inlineSize: 'calc(100% + 46px)',
    textAlign: 'left',
    paddingInlineStart: `${theme.space[900]}`,
    paddingInlineEnd: `${theme.space[250]}`,
    cursor: 'pointer',
    alignItems: 'center',
    ':hover': {
      background: `${theme.color['fill-tertiary-default']}`
    }
  };
});
const StyledPropertyInnerDiv = styled.div.withConfig({
  displayName: "DataPanelProperty__StyledPropertyInnerDiv",
  componentId: "sc-1lbjahd-1"
})(["display:flex;justify-content:space-between;block-size:40px;opacity:", ";"], props => props.internalDisabled ? 0.6 : 1);
const DataPanelProperty = ({
  onDataPropertySelected,
  dataProperty,
  fromDataSourceName,
  disabled,
  hideEditPropertyLink,
  isPropertyTypeCompatible,
  objectTypeId,
  refetchDataSource = emptyFunction,
  isDataSourceFilteredOut,
  requiredPermissionLevel
}) => {
  var _dataProperty$metadat;
  const isUngated = useGetIsUngated();
  const dataPropertyRef = useRef(null);
  const keyDownHandlerRef = useRef(null);
  useEffect(
  // clean up keyDownHandlerRef on unmount
  () => () => {
    if (keyDownHandlerRef.current) {
      window.removeEventListener('keydown', keyDownHandlerRef.current);
    }
  }, []);
  const [isHovered, setIsHovered] = useState(false);
  const trackEvent = useTrackEvent();
  const isGatedToSupportSelectingSensitiveProperties = !useIsUngated(SUPPORT_SELECTING_SENSITIVE_PROPERTIES);
  const dataSensitivityAllowlist = isGatedToSupportSelectingSensitiveProperties ? ['none'] : undefined;
  const enforceFlp = useIsUngated(ENFORCE_FIELD_LEVEL_PERMISSIONS);
  const {
    description,
    label = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.properties.default-label"
    }),
    name
  } = dataProperty;
  const disabledSensitiveProperty = getIsRestrictedSensitiveProperty(dataProperty, dataSensitivityAllowlist);
  const flpDisabled = enforceFlp && !hasFlpAccessToDataProperty(dataProperty, requiredPermissionLevel);
  const isRelativeTimeProperty = ['time_since', 'time_until'].includes(((_dataProperty$metadat = dataProperty.metadata) === null || _dataProperty$metadat === void 0 ? void 0 : _dataProperty$metadat.typeDefinition.dateDisplayHint) || '');
  const internalDisabled = disabled || disabledSensitiveProperty || flpDisabled || !isPropertyTypeCompatible || isRelativeTimeProperty || isDataSourceFilteredOut;
  const handleOnSelectProperty = () => {
    if (internalDisabled) {
      return;
    }
    onDataPropertySelected(dataProperty);
  };
  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      handleOnSelectProperty();
    }
  };
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (internalDisabled) {
      return;
    }
    if (keyDownHandlerRef.current) {
      window.removeEventListener('keydown', keyDownHandlerRef.current);
    }
    keyDownHandlerRef.current = handleKeyDown;
    window.addEventListener('keydown', keyDownHandlerRef.current);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (keyDownHandlerRef.current) {
      window.removeEventListener('keydown', keyDownHandlerRef.current);
    }
    keyDownHandlerRef.current = null;
  };
  const handleOnInfoIconClick = () => trackEvent(Trackables.CLICK_DATA_PANEL_PROPERTY_INFO, {
    action: 'open modal',
    modal: 'PropertyEditModal',
    objectTypeId
  });
  let tooltip = description;
  let shouldUseTooltipHeadingText = true;
  if (flpDisabled) {
    tooltip = getFlpDisabledPropertyMessage(dataProperty.name);
    shouldUseTooltipHeadingText = false;
  } else if (disabledSensitiveProperty) {
    tooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.properties.sensitive-property"
    });
    shouldUseTooltipHeadingText = false;
  } else if (disabled) {
    tooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.properties.select-field"
    });
  } else if (isDataSourceFilteredOut) {
    tooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.properties.filtered-out-data-source"
    });
    shouldUseTooltipHeadingText = false;
  } else if (!isPropertyTypeCompatible || isRelativeTimeProperty) {
    tooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.properties.property-type-disabled"
    });
    shouldUseTooltipHeadingText = false;
  }
  return /*#__PURE__*/_jsx(StyledPropertyDiv, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    children: /*#__PURE__*/_jsx(Tooltip, {
      headingText: shouldUseTooltipHeadingText ? /*#__PURE__*/_jsxs("div", {
        className: "p-bottom-3",
        children: [/*#__PURE__*/_jsx(DataPanelPropertyIcon, {
          dataProperty: dataProperty,
          requiredPermissionLevel: requiredPermissionLevel
        }), label]
      }) : undefined,
      maxInlineSize: "250px",
      title: tooltip,
      use: shouldUseTooltipHeadingText ? 'longform' : undefined,
      placement: "right",
      children: /*#__PURE__*/_jsxs(StyledPropertyInnerDiv, {
        internalDisabled: internalDisabled,
        children: [/*#__PURE__*/_jsxs(UIButton, {
          "data-test-id": `data-panel-data-property-${fromDataSourceName}.${dataProperty.name}`,
          disabled: internalDisabled,
          onClick: handleOnSelectProperty,
          truncate: true,
          truncateStringProps: {
            useFlex: false,
            hideDefaultTooltip: true
          },
          use: "unstyled",
          children: [/*#__PURE__*/_jsx(DataPanelPropertyIcon, {
            dataProperty: dataProperty,
            requiredPermissionLevel: requiredPermissionLevel
          }), /*#__PURE__*/_jsx(Text, {
            as: "span",
            ref: dataPropertyRef,
            children: label
          })]
        }), !hideEditPropertyLink && objectTypeId && isHovered && /*#__PURE__*/_jsx(EditPropertyLink, {
          objectTypeId: objectTypeId,
          propertyName: name,
          onSave: refetchDataSource,
          isUngated: isUngated,
          children: /*#__PURE__*/_jsx(Tooltip, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.properties.info-icon-tooltip"
            }),
            children: /*#__PURE__*/_jsx(IconHolder, {
              icon: InfoIcon,
              paddingBlockStart: "300",
              color: "icon-core-subtle",
              onClick: handleOnInfoIconClick
            })
          })
        })]
      })
    })
  });
};
export default DataPanelProperty;